"use client";

/**
 * ParticipantPanel
 *
 * Three-part rights management for a track or release.
 * Any authenticated user can add a participant.
 * Added participants receive an in-app notification and must accept.
 *
 * Rights pools (each sums to 100 % independently):
 *   Writing     — song composition (writers only)
 *   Publishing  — exploitation / admin (writers + publishers share)
 *   Performance — recorded performance (artists, musicians, producers)
 */

import React, { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import {
  Users, UserPlus, CheckCircle2, AlertTriangle, Clock, Pencil,
  Trash2, Loader2, ChevronDown, ChevronUp, Music2, X, Check,
  Play, Pause, Heart, Gift,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useMediaPlayer } from "@/contexts/media-player-context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage, getInitials } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/auth-context";
import { useAdmin } from "@/hooks/use-admin";
import { useToast } from "@/hooks/use-toast";
import { UserInput, type UserResult } from "@/components/profile/user-input";
import { resolveOrCreatePerson } from "@/services/admin";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import {
  getAgreementForMedia,
  addParticipantToAgreement,
  removeParticipantFromAgreement,
  acceptSplit,
  disputeSplit,
  updateSplits,
  subscribeAgreement,
  type SplitAgreement,
  type SplitParty,
} from "@/services/split-agreements";

import {
  WRITER_ROLES, PUBLISHER_ROLES, PERFORMANCE_ROLES, THANKS_ROLES,
  roleCategory, mapContributorRoleToParticipantRole,
} from "@/lib/split-roles";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function pct(n: number) {
  return n % 1 === 0 ? `${n}%` : `${n.toFixed(1)}%`;
}

function poolTotal(splits: SplitParty[], field: keyof Pick<SplitParty, "writingPercent" | "publishingPercent" | "performancePercent">): number {
  return splits.reduce((s, p) => s + (p[field] || 0), 0);
}

function statusColor(s: string) {
  if (s === "accepted")  return "text-green-500";
  if (s === "disputed")  return "text-red-500";
  return "text-yellow-500";
}

function StatusIcon({ status }: { status: string }) {
  if (status === "accepted")  return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
  if (status === "disputed")  return <AlertTriangle className="h-3.5 w-3.5 text-red-500" />;
  if (status === "proposed")  return <Clock className="h-3.5 w-3.5 text-blue-500" />;
  return <Clock className="h-3.5 w-3.5 text-yellow-500" />;
}

// ─── Props ───────────────────────────────────────────────────────────────────

/** Track info used for the per-track picker on releases — workId, title, optional
 *  trackNumber + audio source so the dialog can offer a play-preview button. */
export interface ParticipantTrack {
  workId: string;
  title: string;
  trackNumber?: number;
  audioUrl?: string;
  mp3Url?: string;
  imageUrl?: string;
  artist?: string;
}

/** Minimal shape of a track/release Contributor credit — offered as
 *  one-click / bulk "promote to rights participant" shortcuts. */
export interface ContributorCredit {
  id: string;
  name: string;
  userId?: string;
  role?: string;
  roles?: string[];
}

interface ParticipantPanelProps {
  mediaId: string;
  mediaType: "work" | "release";
  mediaTitle: string;
  mediaCoverUrl?: string;
  /** Tracks on the release — enables the multi-track picker on `mediaType:'release'`. */
  tracks?: ParticipantTrack[];
  /** Contributors already credited on this track/release — lets someone
   *  already listed as a Contributor be promoted straight into Rights &
   *  Splits instead of re-entering their name (Gordon 2026-09-30: "Turn
   *  Contributors into Rights & Splits owners"). */
  contributors?: ContributorCredit[];
  /** Fired once a rights participant is successfully added via the manual
   *  Add Participant flow (not the Contributor-promote paths, since those
   *  are already Contributors) — lets the parent sync them onto the track's
   *  Contributors list too (Gordon 2026-09-30: "New Contributor should be
   *  added to track"). */
  onParticipantAdded?: (party: { userId: string; name: string; role: string }) => void;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ParticipantPanel({ mediaId, mediaType, mediaTitle, mediaCoverUrl, tracks = [], contributors = [], onParticipantAdded }: ParticipantPanelProps) {
  const { user, userProfile } = useAuth();
  const { isAdmin } = useAdmin();
  const tSplit = useTranslations("music.splits");
  const { toast } = useToast();

  const [agreement, setAgreement] = useState<SplitAgreement | null>(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Add-participant form state
  const [selectedUser, setSelectedUser] = useState<UserResult | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [role, setRole] = useState("");
  const [writingPct, setWritingPct] = useState(0);
  const [publishingPct, setPublishingPct] = useState(0);
  const [performancePct, setPerformancePct] = useState(0);
  const [salesPct, setSalesPct] = useState(0);   // share of sales %, the money
  const [hasPublishers, setHasPublishers] = useState(false);

  // Edit-participant form state — update an already-added party's role/%
  // (Gordon 2026-09-30: "Can't update the % of a performing rights").
  const [editingPartyUserId, setEditingPartyUserId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState("");
  const [editWritingPct, setEditWritingPct] = useState(0);
  const [editPublishingPct, setEditPublishingPct] = useState(0);
  const [editPerformancePct, setEditPerformancePct] = useState(0);
  const [editSalesPct, setEditSalesPct] = useState(0);
  const [editSaving, setEditSaving] = useState(false);

  // Bulk-promote form state — stage every not-yet-promoted Contributor with
  // a role + % at once instead of one-at-a-time.
  const [bulkPromoteOpen, setBulkPromoteOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState<Record<string, {
    include: boolean; role: string; creditKind: "split" | "thanks";
    writingPct: number; publishingPct: number; performancePct: number;
  }>>({});
  const [bulkSaving, setBulkSaving] = useState(false);

  // "Create the person" fallback — the typed name has no matching registered
  // user (Gordon 2026-09-30: "add and notify. if the user doesn't exist
  // create the person... sometimes artist and user is the same name").
  const [creatingPerson, setCreatingPerson] = useState(false);
  /** 'split' = full rights allocation (writing/publishing/performance).
   *  'thanks' = honorary credit, no copyright or revenue %, no rights allocated. */
  const [creditKind, setCreditKind] = useState<"split" | "thanks">("split");
  /** For releases: which tracks the participant worked on. Defaults to all. */
  const [selectedTrackIds, setSelectedTrackIds] = useState<Set<string>>(
    () => new Set(tracks.map(t => t.workId).filter(Boolean))
  );
  const isReleaseWithTracks = mediaType === "release" && tracks.length > 0;
  // Bring in the BB media player so each row in the picker can preview audio.
  const { playTrack, currentTrack, isPlaying, togglePlay } = useMediaPlayer();

  // Subscribe to real-time agreement updates
  useEffect(() => {
    if (!mediaId) return;
    setLoading(true);
    const unsub = subscribeAgreement(mediaId, (ag) => {
      setAgreement(ag);
      setLoading(false);
    });
    return unsub;
  }, [mediaId]);

  // Detect if publishers are in the current splits when agreement loads
  useEffect(() => {
    if (agreement) {
      setHasPublishers(agreement.hasPublishers ?? false);
    }
  }, [agreement]);

  // When the credit kind flips, drop any selected role that belongs to the
  // other pool — keeps the dropdown honest (thanks roles ⇄ rights roles).
  useEffect(() => {
    if (!role) return;
    const isThanksRole = THANKS_ROLES.includes(role);
    if (creditKind === "thanks" && !isThanksRole) setRole("");
    if (creditKind === "split"  &&  isThanksRole) setRole("");
  }, [creditKind, role]);

  const splits: SplitParty[] = agreement?.splits ?? [];

  // Remaining capacity in each pool (considering existing splits)
  const usedW   = poolTotal(splits, "writingPercent");
  const usedPub = poolTotal(splits, "publishingPercent");
  const usedPerf = poolTotal(splits, "performancePercent");
  const remW   = Math.max(0, 100 - usedW);
  const remPub = Math.max(0, 100 - usedPub);
  const remPerf = Math.max(0, 100 - usedPerf);
  // Share of sales already AGREED by others (basis points). Only accepted terms pay.
  const usedSalesBp = splits.filter(s => s.status === "accepted").reduce((a, s) => a + ((s as any).salesShareBp || 0), 0);
  const remSales = Math.max(0, 100 - usedSalesBp / 100);

  // Contributors not yet a rights participant on this track — promotable
  // via a one-click prefill (single) or the bulk dialog below.
  const linkedUserIds = new Set(splits.map(s => s.userId));
  const promoCandidates: ContributorCredit[] = contributors
    .filter(c => c.userId && !linkedUserIds.has(c.userId))
    .filter((c, idx, arr) => arr.findIndex(x => x.userId === c.userId) === idx);

  // Derive which pools are enabled from existing data + role hints
  const cat = role ? roleCategory(role) : null;

  // Auto-set category-appropriate percentages when role changes
  useEffect(() => {
    if (!cat) return;
    if (cat === "writing") {
      if (publishingPct === 0) setPublishingPct(Math.min(remPub, writingPct || 0));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const resetForm = () => {
    setSelectedUser(null);
    setUserSearch("");
    setRole("");
    setWritingPct(0);
    setPublishingPct(0);
    setPerformancePct(0);
    setSalesPct(0);
  };

  /** One-click promote: pre-fill the existing Add Participant dialog from an
   *  already-credited Contributor instead of re-searching their name. */
  const startPromote = (c: ContributorCredit) => {
    if (!c.userId) return;
    const mapped = mapContributorRoleToParticipantRole(c.roles && c.roles.length ? c.roles : (c.role ? [c.role] : []));
    setSelectedUser({ id: c.userId, displayName: c.name, profileImageUrl: "" });
    setUserSearch(c.name);
    setRole(mapped.role);
    setCreditKind(mapped.creditKind);
    setWritingPct(0);
    setPublishingPct(0);
    setPerformancePct(0);
    setSalesPct(0);
    setAddOpen(true);
  };

  /** No search result matched the typed name — resolve it against an existing
   *  artist profile (claimed → their real account) or create a claimable
   *  placeholder, then select it as if it came from the search. */
  const handleCreateNewPerson = async () => {
    const name = userSearch.trim();
    if (!name) return;
    setCreatingPerson(true);
    try {
      const person = await resolveOrCreatePerson(name);
      const resolved: UserResult = { id: person.id, displayName: person.displayName, profileImageUrl: person.profileImageUrl, email: person.email };
      setSelectedUser(resolved);
      setUserSearch(resolved.displayName);
      toast({ title: "Account ready", description: `${resolved.displayName} can now be added — they'll get a claimable account if they weren't already registered.` });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Couldn't create account", description: err?.message || String(err) });
    } finally {
      setCreatingPerson(false);
    }
  };

  const handleAddParticipant = async () => {
    if (!user || !role) return;
    // A name was typed but never explicitly picked/created (Gordon
    // 2026-09-30: "database is not updating" — the button was silently
    // no-op'ing here because selectedUser stayed null). Resolve it the same
    // way the "Create an account" link does, right at submit time, so
    // clicking Add & Notify always does something instead of nothing.
    let person = selectedUser;
    if (!person) {
      const name = userSearch.trim();
      if (!name) return;
      setSaving(true);
      try {
        const resolved = await resolveOrCreatePerson(name);
        person = { id: resolved.id, displayName: resolved.displayName, profileImageUrl: resolved.profileImageUrl, email: resolved.email };
        setSelectedUser(person);
      } catch (err: any) {
        toast({ variant: "destructive", title: "Couldn't resolve or create that person", description: err?.message || String(err) });
        setSaving(false);
        return;
      }
      setSaving(false);
    }
    const isThanks = creditKind === "thanks";

    if (!isThanks) {
      // Splits path — needs at least one positive allocation
      if (writingPct <= 0 && publishingPct <= 0 && performancePct <= 0) {
        toast({ variant: "destructive", title: "No rights allocated", description: "Allocate at least one type of right (writing, publishing, or performance), or switch to 'General thanks' (no rights)." });
        return;
      }
      if (writingPct + usedW > 100.01) {
        toast({ variant: "destructive", title: "Writing rights overflow", description: `Only ${pct(remW)} remaining in the writing pool.` });
        return;
      }
      if (publishingPct + usedPub > 100.01) {
        toast({ variant: "destructive", title: "Publishing rights overflow", description: `Only ${pct(remPub)} remaining in the publishing pool.` });
        return;
      }
      if (performancePct + usedPerf > 100.01) {
        toast({ variant: "destructive", title: "Performance rights overflow", description: `Only ${pct(remPerf)} remaining in the performance pool.` });
        return;
      }
    }

    // Don't add same user twice on the SAME mediaId (release-as-whole) — the
    // per-track fan-out below has its own dedupe path via the service.
    if (!isReleaseWithTracks && splits.some(s => s.userId === person.id)) {
      toast({ variant: "destructive", title: "Already added", description: `${person.displayName} is already a participant.` });
      return;
    }

    if (isReleaseWithTracks && selectedTrackIds.size === 0) {
      toast({ variant: "destructive", title: "Pick at least one track", description: "Choose which tracks this person worked on." });
      return;
    }

    setSaving(true);
    try {
      const party = {
        userId: person.id,
        userName: person.displayName,
        // Firestore rejects `undefined` field values — use '' when there's no avatar.
        userAvatar: person.profileImageUrl || '',
        role: isThanks ? `Thanks — ${role}` : role,
        writingPercent: isThanks ? 0 : writingPct,
        publishingPercent: isThanks ? 0 : publishingPct,
        performancePercent: isThanks ? 0 : performancePct,
        salesShareBp: isThanks ? 0 : Math.round(salesPct * 100),
        // Stamp the credit kind so downstream UI can distinguish honorary credits
        // from rights-bearing splits.
        creditKind: isThanks ? "thanks" as const : "split" as const,
      };

      const currentHasPublishers = hasPublishers || (!isThanks && PUBLISHER_ROLES.includes(role));

      // Multi-track fan-out for release context: one agreement per track.
      const targets: { id: string; type: "work" | "release"; title: string; cover?: string }[] =
        isReleaseWithTracks
          ? tracks
              .filter(t => selectedTrackIds.has(t.workId))
              .map(t => ({ id: t.workId, type: "work" as const, title: t.title, cover: t.imageUrl }))
          : [{ id: mediaId, type: mediaType, title: mediaTitle, cover: mediaCoverUrl }];

      let lastAgreementId: string | undefined;
      let succeeded = 0;
      let lastError: any = null;
      if (targets.length === 0) {
        throw new Error("No track selected — pick at least one track to credit.");
      }
      for (const tgt of targets) {
        if (!tgt.id) { lastError = new Error("Track is missing an ID."); continue; }
        try {
          const { agreementId } = await addParticipantToAgreement(
            tgt.id, tgt.type, tgt.title, tgt.cover ?? "",
            party,
            {
              hasPublishers: currentHasPublishers,
              writingEnabled: !isThanks && (writingPct > 0 || usedW > 0),
              publishingEnabled: !isThanks && (publishingPct > 0 || usedPub > 0),
              performanceEnabled: !isThanks && (performancePct > 0 || usedPerf > 0),
              createdBy: user.uid,
              createdByName: userProfile?.displayName || user.email || "Unknown",
            }
          );
          lastAgreementId = agreementId;
          succeeded++;
        } catch (err: any) {
          lastError = err;
          console.error("addParticipantToAgreement failed for", tgt.id, err);
        }
      }
      const agreementId = lastAgreementId;
      if (!agreementId) {
        // Surface the real reason (e.g. permissions / validation) instead of a
        // generic message, so the failure is actionable.
        throw new Error(lastError?.message || "Failed to add participant to any track.");
      }

      // Send in-app (+ email) notification to the added participant
      const rightLines = [
        writingPct > 0     ? `Writing: ${pct(writingPct)}`     : null,
        publishingPct > 0  ? `Publishing: ${pct(publishingPct)}` : null,
        performancePct > 0 ? `Performance: ${pct(performancePct)}` : null,
      ].filter(Boolean);

      await user.getIdToken().then(token =>
        fetch("/api/notify/contributors", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            work: {
              id: mediaId,
              title: mediaTitle,
              coverArtUrl: mediaCoverUrl,
              workUrl: `${window.location.origin}/music/${mediaId}`,
            },
            agreementId,
            contributors: [{
              id: person.id,
              name: person.displayName,
              roles: [role],
              userId: person.id,
              email: person.email,
              rightsSummary: rightLines.join(", "),
            }],
            publishedBy: userProfile?.displayName || user.email || "A contributor",
          }),
        })
      ).catch(() => { /* non-fatal */ });

      // Someone can be given rights without ever having been credited first
      // (Gordon 2026-09-30: "New Contributor should be added to track") —
      // sync them onto the track's Contributors list too, merging into an
      // existing credit if they already have one.
      onParticipantAdded?.({ userId: person.id, name: person.displayName, role: isThanks ? `Thanks — ${role}` : role });

      const trackPart = isReleaseWithTracks
        ? ` to ${succeeded} track${succeeded === 1 ? "" : "s"}`
        : "";
      const kindPart = isThanks ? " (general thanks — no rights)" : "";
      toast({
        title: "Participant added",
        description: `${person.displayName}${trackPart}${kindPart} — they have been notified.`,
      });

      setAddOpen(false);
      resetForm();
      setCreditKind("split");
      setSelectedTrackIds(new Set(tracks.map(t => t.workId).filter(Boolean)));
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const openBulkPromote = () => {
    const rows: typeof bulkRows = {};
    for (const c of promoCandidates) {
      const mapped = mapContributorRoleToParticipantRole(c.roles && c.roles.length ? c.roles : (c.role ? [c.role] : []));
      rows[c.id] = { include: true, role: mapped.role, creditKind: mapped.creditKind, writingPct: 0, publishingPct: 0, performancePct: 0 };
    }
    setBulkRows(rows);
    setBulkPromoteOpen(true);
  };

  const bulkIncluded = promoCandidates.filter(c => bulkRows[c.id]?.include);
  const bulkTotals = bulkIncluded.reduce((acc, c) => {
    const r = bulkRows[c.id];
    if (!r || r.creditKind === "thanks") return acc;
    return { w: acc.w + r.writingPct, pub: acc.pub + r.publishingPct, perf: acc.perf + r.performancePct };
  }, { w: 0, pub: 0, perf: 0 });
  const projectedW = usedW + bulkTotals.w;
  const projectedPub = usedPub + bulkTotals.pub;
  const projectedPerf = usedPerf + bulkTotals.perf;

  const handleBulkPromote = async () => {
    if (!user || bulkIncluded.length === 0) return;
    if (projectedW > 100.01 || projectedPub > 100.01 || projectedPerf > 100.01) {
      toast({ variant: "destructive", title: "Rights overflow", description: "One or more pools would exceed 100% — adjust the percentages before promoting." });
      return;
    }
    setBulkSaving(true);
    let lastAgreementId: string | undefined;
    let succeeded = 0;
    const notified: { id: string; name: string; roles: string[]; userId: string; rightsSummary: string }[] = [];
    for (const c of bulkIncluded) {
      const row = bulkRows[c.id];
      const isThanks = row.creditKind === "thanks";
      const currentHasPublishers = hasPublishers || (!isThanks && PUBLISHER_ROLES.includes(row.role));
      try {
        const { agreementId } = await addParticipantToAgreement(
          mediaId, mediaType, mediaTitle, mediaCoverUrl || "",
          {
            userId: c.userId!,
            userName: c.name,
            userAvatar: "",
            role: isThanks ? `Thanks — ${row.role}` : row.role,
            writingPercent: isThanks ? 0 : row.writingPct,
            publishingPercent: isThanks ? 0 : row.publishingPct,
            performancePercent: isThanks ? 0 : row.performancePct,
          },
          {
            hasPublishers: currentHasPublishers,
            writingEnabled: !isThanks && (row.writingPct > 0 || usedW > 0),
            publishingEnabled: !isThanks && (row.publishingPct > 0 || usedPub > 0),
            performanceEnabled: !isThanks && (row.performancePct > 0 || usedPerf > 0),
            createdBy: user.uid,
            createdByName: userProfile?.displayName || user.email || "Unknown",
          }
        );
        lastAgreementId = agreementId;
        succeeded++;
        const rightLines = [
          !isThanks && row.writingPct > 0 ? `Writing: ${pct(row.writingPct)}` : null,
          !isThanks && row.publishingPct > 0 ? `Publishing: ${pct(row.publishingPct)}` : null,
          !isThanks && row.performancePct > 0 ? `Performance: ${pct(row.performancePct)}` : null,
        ].filter(Boolean) as string[];
        notified.push({ id: c.userId!, name: c.name, roles: [row.role], userId: c.userId!, rightsSummary: rightLines.join(", ") });
      } catch (err) {
        console.error("bulk promote failed for", c.name, err);
      }
    }

    if (succeeded > 0 && lastAgreementId) {
      await user.getIdToken().then(token =>
        fetch("/api/notify/contributors", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            work: { id: mediaId, title: mediaTitle, coverArtUrl: mediaCoverUrl, workUrl: `${window.location.origin}/music/${mediaId}` },
            agreementId: lastAgreementId,
            contributors: notified,
            publishedBy: userProfile?.displayName || user.email || "A contributor",
          }),
        })
      ).catch(() => { /* non-fatal */ });
    }

    toast({
      title: `Promoted ${succeeded} of ${bulkIncluded.length} contributor${bulkIncluded.length === 1 ? "" : "s"}`,
      description: succeeded > 0 ? "They have been notified." : "Nothing was promoted — check for errors and try again.",
      variant: succeeded > 0 ? undefined : "destructive",
    });
    setBulkSaving(false);
    setBulkPromoteOpen(false);
  };

  const handleAccept = async () => {
    if (!user || !agreement?.id) return;
    try {
      await acceptSplit(agreement.id, user.uid);
      toast({ title: "Participation accepted", description: "Your rights are confirmed." });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    }
  };

  const handleDispute = async () => {
    if (!user || !agreement?.id) return;
    try {
      await disputeSplit(agreement.id, user.uid, "Disputed by participant");
      toast({ title: "Dispute filed", description: "The agreement owner will be notified." });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    }
  };

  // The owner answers a claim or a counter-proposal from this person.
  const handleOwnerAnswer = async (partyUserId: string, accept: boolean) => {
    if (!agreement?.id) return;
    try {
      if (accept) await acceptSplit(agreement.id, partyUserId);
      else await disputeSplit(agreement.id, partyUserId, "Declined by owner");
      toast({ title: accept ? tSplit("agreed") : tSplit("declined") });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    }
  };

  const handleRemove = async (userId: string) => {
    if (!user || !agreement?.id) return;
    try {
      await removeParticipantFromAgreement(agreement.id, userId);
      toast({ title: "Participant removed" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    }
  };

  // ── Edit an already-added party's role/percentages ──────────────────────
  const startEditParty = (party: SplitParty) => {
    setEditingPartyUserId(party.userId);
    setEditRole(party.role.replace(/^Thanks — /, ""));
    setEditWritingPct(party.writingPercent || 0);
    setEditPublishingPct(party.publishingPercent || 0);
    setEditPerformancePct(party.performancePercent || 0);
    setEditSalesPct(((party as any).salesShareBp || 0) / 100);
  };
  const cancelEditParty = () => setEditingPartyUserId(null);
  const saveEditParty = async () => {
    if (!agreement?.id || !editingPartyUserId) return;
    const editingParty = splits.find(s => s.userId === editingPartyUserId);
    if (!editingParty) return;

    // Remaining capacity excludes this party's OWN current share, so moving
    // their slider doesn't get capped by the % they already hold.
    const otherW    = usedW    - (editingParty.writingPercent || 0);
    const otherPub  = usedPub  - (editingParty.publishingPercent || 0);
    const otherPerf = usedPerf - (editingParty.performancePercent || 0);
    if (editWritingPct + otherW > 100.01) {
      toast({ variant: "destructive", title: "Writing rights overflow", description: `Only ${pct(Math.max(0, 100 - otherW))} available for ${editingParty.userName} in the writing pool.` });
      return;
    }
    if (editPublishingPct + otherPub > 100.01) {
      toast({ variant: "destructive", title: "Publishing rights overflow", description: `Only ${pct(Math.max(0, 100 - otherPub))} available for ${editingParty.userName} in the publishing pool.` });
      return;
    }
    if (editPerformancePct + otherPerf > 100.01) {
      toast({ variant: "destructive", title: "Performance rights overflow", description: `Only ${pct(Math.max(0, 100 - otherPerf))} available for ${editingParty.userName} in the performance pool.` });
      return;
    }

    setEditSaving(true);
    try {
      const isThanks = editingParty.role.startsWith("Thanks — ");
      const nextSplits = splits.map(s => s.userId === editingPartyUserId
        ? {
            ...s,
            role: isThanks ? `Thanks — ${editRole}` : editRole,
            writingPercent: editWritingPct,
            publishingPercent: editPublishingPct,
            performancePercent: editPerformancePct,
            salesShareBp: isThanks ? 0 : Math.round(editSalesPct * 100),
          }
        : s);
      await updateSplits(agreement.id, nextSplits);
      toast({ title: "Split updated", description: tSplit("changeSent", { name: editingParty.userName }) });
      setEditingPartyUserId(null);
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setEditSaving(false);
    }
  };

  const myEntry = splits.find(s => s.userId === user?.uid);
  // The server records the real owner; createdBy can be a claimant on claim-created agreements.
  const isCreator = ((agreement as any)?.ownerUserId ?? agreement?.createdBy) === user?.uid;
  const canManage = !!(user); // any logged-in user can add participants
  const canEditSplits = isCreator || isAdmin; // changing someone's % is a creator/admin action
  // Whose turn is it on this person's row? Every share needs both sides to agree.
  const awaitingLabel = (party: SplitParty, isMe: boolean): string | null => {
    const aw = (party as any).awaiting as "owner" | "party" | null | undefined;
    if (!aw) return null;
    if ((aw === "party" && isMe) || (aw === "owner" && canEditSplits && !isMe)) return tSplit("waitingForYou");
    return aw === "owner" ? tSplit("waitingForOwner") : tSplit("waitingForPerson", { name: party.userName });
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" />
          Participants & Rights
        </h3>
        <div className="flex items-center gap-1.5">
          {canManage && promoCandidates.length > 0 && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={openBulkPromote}>
              <Users className="h-3.5 w-3.5" />
              Promote {promoCandidates.length} Contributor{promoCandidates.length === 1 ? "" : "s"}
            </Button>
          )}
          {canManage && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAddOpen(true)}>
              <UserPlus className="h-3.5 w-3.5" />
              Add Participant
            </Button>
          )}
        </div>
      </div>

      {/* One-click promote chips — already-credited Contributors who aren't
          rights participants yet. Click to prefill the Add Participant
          dialog with their name/account instead of re-searching. */}
      {canManage && promoCandidates.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">From Contributors:</span>
          {promoCandidates.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => startPromote(c)}
              title={`Promote ${c.name} to a rights participant`}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-primary/40 text-primary/80 hover:border-primary hover:text-primary px-2 py-0.5 text-[11px] transition-colors"
            >
              <UserPlus className="h-2.5 w-2.5" /> {c.name}
            </button>
          ))}
        </div>
      )}

      {/* Rights pool summary bars */}
      {splits.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { label: "Writing", used: usedW, color: "bg-blue-500" },
            { label: "Publishing", used: usedPub, color: "bg-purple-500" },
            { label: "Performance", used: usedPerf, color: "bg-green-500" },
          ].map(({ label, used, color }) => (
            <div key={label} className="space-y-1">
              <div className="flex justify-between text-[11px] text-muted-foreground">
                <span>{label}</span>
                <span className={used > 100.01 ? "text-red-500 font-semibold" : ""}>
                  {pct(used)} / 100%
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div
                  className={cn("h-full rounded-full transition-all", color, used > 100.01 ? "bg-red-500" : "")}
                  style={{ width: `${Math.min(100, used)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Participants list */}
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : splits.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          <Users className="h-7 w-7 mx-auto mb-2 opacity-30" />
          <p>No participants yet.</p>
          {canManage && (
            <p className="text-xs mt-1">Click "Add Participant" to invite collaborators and set rights.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {splits.map((party, i) => {
            const isMe = party.userId === user?.uid;
            const isEditingThis = editingPartyUserId === party.userId;
            // Remaining pool capacity excluding THIS party's own current share.
            const otherW    = usedW    - (party.writingPercent || 0);
            const otherPub  = usedPub  - (party.publishingPercent || 0);
            const otherPerf = usedPerf - (party.performancePercent || 0);
            const editRemW    = Math.max(0, 100 - otherW);
            const editRemPub  = Math.max(0, 100 - otherPub);
            const editRemPerf = Math.max(0, 100 - otherPerf);
            const editRemSales = Math.max(0, 100 - (usedSalesBp - (party.status === "accepted" ? ((party as any).salesShareBp || 0) : 0)) / 100);
            return (
              <div
                key={`${party.userId}-${i}`}
                className={cn(
                  "rounded-lg border p-3 space-y-2 transition-colors",
                  isMe ? "border-primary/30 bg-primary/5" : "bg-card"
                )}
              >
                {/* Party header */}
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8 flex-shrink-0">
                    {party.userAvatar && <AvatarImage src={party.userAvatar} />}
                    <AvatarFallback className="text-[11px]" name={party.userName}>{getInitials(party.userName)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {party.userName}{isMe ? " (You)" : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">{party.role}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <StatusIcon status={party.status} />
                    <span className={cn("text-[11px] font-medium", (party as any).awaiting ? "text-foreground" : cn("capitalize", statusColor(party.status)))}>
                      {awaitingLabel(party, isMe) ?? party.status}
                    </span>
                  </div>

                  {/* Accept / Dispute for pending participants */}
                  {isMe && (party as any).awaiting === "party" && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-green-600 hover:text-green-500" title={tSplit("accept")} aria-label={tSplit("accept")} onClick={handleAccept}>
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600 hover:text-red-500" title={tSplit("decline")} aria-label={tSplit("decline")} onClick={handleDispute}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}

                  {/* Owner answers a claim or a counter-proposal */}
                  {!isMe && canEditSplits && (party as any).awaiting === "owner" && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-green-600 hover:text-green-500" title={tSplit("accept")} aria-label={tSplit("accept")} onClick={() => handleOwnerAnswer(party.userId, true)}>
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600 hover:text-red-500" title={tSplit("decline")} aria-label={tSplit("decline")} onClick={() => handleOwnerAnswer(party.userId, false)}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}

                  {/* Edit role/% (creator or admin — changes another party's
                      share, so it's not a self-serve action) */}
                  {canEditSplits && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-muted-foreground/40 hover:text-primary flex-shrink-0"
                      title="Edit role & rights %"
                      onClick={() => (isEditingThis ? cancelEditParty() : startEditParty(party))}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}

                  {/* Remove (creator or self) */}
                  {(isCreator || isMe) && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-muted-foreground/40 hover:text-destructive flex-shrink-0"
                      title="Remove participant"
                      onClick={() => handleRemove(party.userId)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>

                {isEditingThis ? (
                  /* ── Inline editor: change role + rights % ── */
                  <div className="pl-10 space-y-2.5">
                    <div>
                      <Label className="text-[10px] text-muted-foreground mb-1 block">Role</Label>
                      <select
                        value={editRole}
                        onChange={e => setEditRole(e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-input rounded-md bg-background text-foreground"
                      >
                        <option value="">Select role…</option>
                        <optgroup label="✍ Writing">{WRITER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}</optgroup>
                        <optgroup label="📋 Publishing">{PUBLISHER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}</optgroup>
                        <optgroup label="🎤 Performance">{PERFORMANCE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}</optgroup>
                        <optgroup label="🙌 Thanks">{THANKS_ROLES.map(r => <option key={r} value={r}>{r}</option>)}</optgroup>
                      </select>
                    </div>
                    {[
                      { label: "Writing", value: editWritingPct, set: setEditWritingPct, rem: editRemW, color: "bg-blue-500" },
                      { label: "Publishing", value: editPublishingPct, set: setEditPublishingPct, rem: editRemPub, color: "bg-purple-500" },
                      { label: "Performance", value: editPerformancePct, set: setEditPerformancePct, rem: editRemPerf, color: "bg-green-500" },
                    ].map(({ label, value, set, rem, color }) => (
                      <div key={label} className="flex items-center gap-2">
                        <span className="w-16 flex-shrink-0 text-[11px] text-muted-foreground">{label}</span>
                        <Input
                          type="number"
                          min={0}
                          max={rem}
                          step={0.5}
                          value={value || ""}
                          placeholder="0"
                          className="h-7 text-xs w-16"
                          onChange={e => set(Math.min(rem, Math.max(0, Number(e.target.value))))}
                        />
                        <span className="text-[10px] text-muted-foreground">% · {pct(rem)} left</span>
                        <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                          <div className={cn("h-full rounded-full transition-all", color)} style={{ width: `${Math.min(100, (100 - rem) + value)}%` }} />
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <span className="w-16 flex-shrink-0 text-[11px] text-muted-foreground">{tSplit("salesShare")}</span>
                      <Input
                        type="number"
                        min={0}
                        max={editRemSales}
                        step={0.5}
                        value={editSalesPct || ""}
                        placeholder="0"
                        aria-label={tSplit("salesShare")}
                        className="h-7 text-xs w-16"
                        onChange={e => setEditSalesPct(Math.min(editRemSales, Math.max(0, Number(e.target.value))))}
                      />
                      <span className="text-[10px] text-muted-foreground">% · {pct(editRemSales)} left</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">{tSplit("salesShareHint")}</p>
                    <div className="flex gap-2 justify-end pt-1">
                      <Button type="button" size="sm" variant="ghost" onClick={cancelEditParty} disabled={editSaving}>Cancel</Button>
                      <Button type="button" size="sm" onClick={saveEditParty} disabled={editSaving || !editRole}>
                        {editSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                  {/* Rights breakdown */}
                  <div className="grid grid-cols-3 gap-2 pl-10">
                    {[
                      { label: "Writing", val: party.writingPercent, color: "text-blue-400" },
                      { label: "Publishing", val: party.publishingPercent, color: "text-purple-400" },
                      { label: "Performance", val: party.performancePercent, color: "text-green-400" },
                    ].map(({ label, val, color }) => (
                      <div key={label} className="text-center">
                        <p className={cn("text-xs font-semibold", val > 0 ? color : "text-muted-foreground/30")}>
                          {val > 0 ? pct(val) : "—"}
                        </p>
                        <p className="text-[10px] text-muted-foreground/60">{label}</p>
                      </div>
                    ))}
                  </div>
                  {((party as any).salesShareBp || 0) > 0 && (
                    <p className="pl-10 text-[11px] text-foreground">
                      {tSplit("salesShare")}: <span className="font-semibold">{pct(((party as any).salesShareBp || 0) / 100)}</span>
                    </p>
                  )}
                  {(party as any).proposal && (() => {
                    const pr = (party as any).proposal;
                    return (
                      <p className="pl-10 text-[11px] text-foreground">
                        <span className="font-medium">{tSplit("proposedChange")}:</span>{" "}
                        {pr.role} · Writing {pct(pr.writingPercent || 0)} · Publishing {pct(pr.publishingPercent || 0)} · Performance {pct(pr.performancePercent || 0)}
                        {pr.salesShareBp ? ` · ${tSplit("salesShare")} ${pct(pr.salesShareBp / 100)}` : ""}
                      </p>
                    );
                  })()}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Agreement status chip */}
      {agreement && (
        <p className="text-[11px] text-muted-foreground text-right">
          Agreement:{" "}
          <span className={cn("font-medium",
            agreement.status === "active"   ? "text-green-500"  :
            agreement.status === "disputed" ? "text-red-500"    :
            agreement.status === "frozen"   ? "text-blue-500"   : "text-yellow-500"
          )}>
            {agreement.status}
          </span>
          {" "}— {splits.length} participant{splits.length !== 1 ? "s" : ""}
        </p>
      )}

      {/* ── Add Participant Dialog ─────────────────────────────────────────── */}
      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Add Participant
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {/* Track info */}
            <div className="flex items-center gap-2 pb-2 border-b border-border/50">
              {mediaCoverUrl && (
                <div className="w-9 h-9 flex-shrink-0 rounded overflow-hidden">
                  <Image src={mediaCoverUrl} alt={mediaTitle} width={36} height={36} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{mediaTitle}</p>
                <p className="text-xs text-muted-foreground">Participants must accept before rights are active.</p>
              </div>
            </div>

            {/* User search */}
            <div>
              <Label className="text-xs mb-1 block">Search user *</Label>
              <UserInput
                value={userSearch}
                onChange={setUserSearch}
                onUserSelect={(u) => { setSelectedUser(u); setUserSearch(u.displayName); }}
                placeholder="Search by name or email…"
                showAll
              />
              {selectedUser && (
                <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                  {selectedUser.profileImageUrl && (
                    <Image src={selectedUser.profileImageUrl} alt="" width={16} height={16} className="rounded-full" />
                  )}
                  <span className="text-primary font-medium">{selectedUser.displayName}</span>
                  {selectedUser.email && <span>· {selectedUser.email}</span>}
                </div>
              )}
              {/* No match picked yet but a name is typed — offer to create the
                  account instead of blocking the add. */}
              {!selectedUser && userSearch.trim().length > 1 && (
                <button
                  type="button"
                  onClick={handleCreateNewPerson}
                  disabled={creatingPerson}
                  className="mt-1.5 text-[11px] text-primary hover:underline disabled:opacity-50 disabled:cursor-wait"
                >
                  {creatingPerson ? "Creating account…" : `Can't find them? Create an account for "${userSearch.trim()}"`}
                </button>
              )}
            </div>

            {/* ── Credit kind toggle — Split (rights) vs Thanks (honorary) ── */}
            <div>
              <Label className="text-xs mb-1.5 block">Credit type *</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setCreditKind("split")}
                  className={cn(
                    "rounded-lg border-2 p-3 text-left transition-colors",
                    creditKind === "split"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/40"
                  )}
                >
                  <p className="text-xs font-bold flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5" /> Split
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Carries copyright + performance rights and a % of revenue.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setCreditKind("thanks")}
                  className={cn(
                    "rounded-lg border-2 p-3 text-left transition-colors",
                    creditKind === "thanks"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/40"
                  )}
                >
                  <p className="text-xs font-bold flex items-center gap-1.5">
                    <Gift className="h-3.5 w-3.5" /> General thanks
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Honorary credit — no copyright, no performance, no revenue %.
                  </p>
                </button>
              </div>
            </div>

            {/* ── Track picker (releases only) ── */}
            {isReleaseWithTracks && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs">Which tracks were they on? *</Label>
                  <button
                    type="button"
                    className="text-[10px] text-primary hover:underline"
                    onClick={() => {
                      if (selectedTrackIds.size === tracks.length) setSelectedTrackIds(new Set());
                      else setSelectedTrackIds(new Set(tracks.map(t => t.workId).filter(Boolean)));
                    }}
                  >
                    {selectedTrackIds.size === tracks.length ? "Deselect all" : "Select all"}
                  </button>
                </div>
                <div className="max-h-56 overflow-y-auto rounded-lg border border-border bg-muted/20 divide-y divide-border/60">
                  {tracks.map((t) => {
                    const checked = selectedTrackIds.has(t.workId);
                    const audioSrc = t.audioUrl || t.mp3Url || "";
                    const isCurrent = currentTrack?.id === t.workId;
                    const isPlayingThis = isCurrent && isPlaying;
                    const canPlay = Boolean(audioSrc);
                    return (
                      <div key={t.workId} className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/40">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!canPlay) return;
                            if (isCurrent) togglePlay();
                            else playTrack({
                              id: t.workId, title: t.title,
                              artist: t.artist || "", imageUrl: t.imageUrl || "",
                              audioUrl: audioSrc, duration: 0,
                            } as any);
                          }}
                          disabled={!canPlay}
                          title={canPlay ? (isPlayingThis ? "Pause" : "Play preview") : "No preview available"}
                          className={cn(
                            "h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors",
                            canPlay
                              ? "bg-primary/15 text-primary hover:bg-primary hover:text-primary-foreground"
                              : "bg-muted text-muted-foreground/40 cursor-not-allowed"
                          )}
                        >
                          {isPlayingThis ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
                        </button>
                        <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              setSelectedTrackIds(prev => {
                                const next = new Set(prev);
                                if (v) next.add(t.workId); else next.delete(t.workId);
                                return next;
                              });
                            }}
                          />
                          {t.trackNumber != null && (
                            <span className="text-[10px] text-muted-foreground font-mono w-5 text-right">{t.trackNumber}</span>
                          )}
                          <span className="text-xs truncate flex-1">{t.title}</span>
                          {checked && <Check className="h-3 w-3 text-emerald-500 flex-shrink-0" />}
                        </label>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  One credit will be added per ticked track ({selectedTrackIds.size} of {tracks.length}).
                </p>
              </div>
            )}

            {/* Role */}
            <div>
              <Label className="text-xs mb-1 block">Role *</Label>
              <select
                value={role}
                onChange={e => setRole(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground"
              >
                <option value="">Select role…</option>
                {creditKind === "thanks" ? (
                  // Thanks credits — recognition only, no rights claim
                  <optgroup label="🙌 Thanks">
                    {THANKS_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </optgroup>
                ) : (
                  <>
                    <optgroup label="✍ Writing">
                      {WRITER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </optgroup>
                    <optgroup label="📋 Publishing">
                      {PUBLISHER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </optgroup>
                    <optgroup label="🎤 Performance">
                      {PERFORMANCE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </optgroup>
                  </>
                )}
              </select>
            </div>

            {/* Rights inputs — hidden in 'thanks' mode since it carries no rights */}
            <div className={cn("space-y-3", creditKind === "thanks" && "hidden")}>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium">Rights Allocation</p>

              {/* Writing */}
              <div className="flex items-center gap-3">
                <div className="w-24 flex-shrink-0">
                  <p className="text-xs font-medium text-blue-400">Writing</p>
                  <p className="text-[10px] text-muted-foreground">{pct(remW)} left</p>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      max={remW}
                      step={0.5}
                      value={writingPct || ""}
                      placeholder="0"
                      className="h-8 text-sm w-20"
                      onChange={e => setWritingPct(Math.min(remW, Math.max(0, Number(e.target.value))))}
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                    <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${Math.min(100, (usedW + writingPct))}%` }} />
                    </div>
                    <span className="text-[10px] text-muted-foreground w-16 text-right">{pct(usedW + writingPct)} / 100%</span>
                  </div>
                </div>
              </div>

              {/* Publishing */}
              <div className="flex items-center gap-3">
                <div className="w-24 flex-shrink-0">
                  <p className="text-xs font-medium text-purple-400">Publishing</p>
                  <p className="text-[10px] text-muted-foreground">{pct(remPub)} left</p>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      max={remPub}
                      step={0.5}
                      value={publishingPct || ""}
                      placeholder="0"
                      className="h-8 text-sm w-20"
                      onChange={e => setPublishingPct(Math.min(remPub, Math.max(0, Number(e.target.value))))}
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                    <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full bg-purple-500 transition-all" style={{ width: `${Math.min(100, (usedPub + publishingPct))}%` }} />
                    </div>
                    <span className="text-[10px] text-muted-foreground w-16 text-right">{pct(usedPub + publishingPct)} / 100%</span>
                  </div>
                </div>
              </div>

              {/* Performance */}
              <div className="flex items-center gap-3">
                <div className="w-24 flex-shrink-0">
                  <p className="text-xs font-medium text-green-400">Performance</p>
                  <p className="text-[10px] text-muted-foreground">{pct(remPerf)} left</p>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      max={remPerf}
                      step={0.5}
                      value={performancePct || ""}
                      placeholder="0"
                      className="h-8 text-sm w-20"
                      onChange={e => setPerformancePct(Math.min(remPerf, Math.max(0, Number(e.target.value))))}
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                    <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${Math.min(100, (usedPerf + performancePct))}%` }} />
                    </div>
                    <span className="text-[10px] text-muted-foreground w-16 text-right">{pct(usedPerf + performancePct)} / 100%</span>
                  </div>
                </div>
              </div>

              {/* Share of sales — the money. Credited people get nothing until both sides agree a share. */}
              <div className="flex items-center gap-3">
                <div className="w-24 flex-shrink-0">
                  <p className="text-xs font-medium text-foreground">{tSplit("salesShare")}</p>
                  <p className="text-[10px] text-muted-foreground">{pct(remSales)} left</p>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      max={remSales}
                      step={0.5}
                      value={salesPct || ""}
                      placeholder="0"
                      aria-label={tSplit("salesShare")}
                      className="h-8 text-sm w-20"
                      onChange={e => setSalesPct(Math.min(remSales, Math.max(0, Number(e.target.value))))}
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">{tSplit("salesShareHint")}</p>
                </div>
              </div>

              {/* Publisher note */}
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                <strong>Writing</strong> — song composition (writers only) ·{" "}
                <strong>Publishing</strong> — writers AND publishers share this 100% ·{" "}
                <strong>Performance</strong> — recorded performance (artists, producers, musicians)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setAddOpen(false); resetForm(); }}>Cancel</Button>
            <Button
              onClick={handleAddParticipant}
              // A "General thanks" credit is honorary — it has NO revenue %, so
              // requiring a percentage made the button impossible to enable for
              // thanks (Gordon: "doesn't let me add a participant, button greyed
              // out"). Only require a % for split credits. Also require a track
              // selection on releases so the button reflects every real gate.
              // A typed-but-unselected name is fine — handleAddParticipant
              // resolves/creates it on submit (Gordon 2026-09-30: "database is
              // not updating" — this used to silently no-op instead).
              disabled={
                (!selectedUser && !userSearch.trim()) ||
                !role ||
                (creditKind === "split" && writingPct <= 0 && publishingPct <= 0 && performancePct <= 0) ||
                (isReleaseWithTracks && selectedTrackIds.size === 0) ||
                saving
              }
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UserPlus className="h-3.5 w-3.5 mr-1" />}
              Add &amp; Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk Promote Dialog — stage every un-promoted Contributor at once ── */}
      <Dialog open={bulkPromoteOpen} onOpenChange={(o) => { setBulkPromoteOpen(o); if (!o) setBulkRows({}); }}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Promote Contributors to Rights & Splits
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-1 max-h-[60vh] overflow-y-auto">
            <p className="text-xs text-muted-foreground">
              Role is guessed from their credit — check it, set a % for each right they hold, then promote. Everyone gets notified and must accept.
            </p>

            {promoCandidates.map(c => {
              const row = bulkRows[c.id];
              if (!row) return null;
              const isThanks = row.creditKind === "thanks";
              return (
                <div key={c.id} className={cn("rounded-lg border p-2.5 space-y-2", !row.include && "opacity-50")}>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={row.include}
                      onCheckedChange={(v) => setBulkRows(prev => ({ ...prev, [c.id]: { ...prev[c.id], include: !!v } }))}
                    />
                    <span className="text-sm font-medium flex-1 truncate">{c.name}</span>
                    <select
                      value={row.role}
                      onChange={e => {
                        const nextRole = e.target.value;
                        const nextKind: "split" | "thanks" = THANKS_ROLES.includes(nextRole) ? "thanks" : "split";
                        setBulkRows(prev => ({ ...prev, [c.id]: { ...prev[c.id], role: nextRole, creditKind: nextKind } }));
                      }}
                      className="px-2 py-1 text-xs border border-input rounded-md bg-background text-foreground"
                    >
                      <optgroup label="✍ Writing">{WRITER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}</optgroup>
                      <optgroup label="📋 Publishing">{PUBLISHER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}</optgroup>
                      <optgroup label="🎤 Performance">{PERFORMANCE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}</optgroup>
                      <optgroup label="🙌 Thanks">{THANKS_ROLES.map(r => <option key={r} value={r}>{r}</option>)}</optgroup>
                    </select>
                  </div>
                  {row.include && !isThanks && (
                    <div className="pl-6 grid grid-cols-3 gap-2">
                      {[
                        { key: "writingPct" as const, label: "Writing" },
                        { key: "publishingPct" as const, label: "Publishing" },
                        { key: "performancePct" as const, label: "Performance" },
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center gap-1">
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            step={0.5}
                            value={row[key] || ""}
                            placeholder="0"
                            className="h-7 text-xs w-16"
                            onChange={e => {
                              const v = Math.min(100, Math.max(0, Number(e.target.value)));
                              setBulkRows(prev => ({ ...prev, [c.id]: { ...prev[c.id], [key]: v } }));
                            }}
                          />
                          <span className="text-[10px] text-muted-foreground">% {label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {row.include && isThanks && (
                    <p className="pl-6 text-[10px] text-muted-foreground">General thanks — no copyright, no revenue %.</p>
                  )}
                </div>
              );
            })}

            {/* Projected pool totals across staged rows + existing splits */}
            <div className="grid grid-cols-3 gap-3 pt-1">
              {[
                { label: "Writing", projected: projectedW, color: "bg-blue-500" },
                { label: "Publishing", projected: projectedPub, color: "bg-purple-500" },
                { label: "Performance", projected: projectedPerf, color: "bg-green-500" },
              ].map(({ label, projected, color }) => (
                <div key={label} className="space-y-1">
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>{label}</span>
                    <span className={projected > 100.01 ? "text-red-500 font-semibold" : ""}>{pct(projected)} / 100%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className={cn("h-full rounded-full transition-all", color, projected > 100.01 ? "bg-red-500" : "")} style={{ width: `${Math.min(100, projected)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkPromoteOpen(false)} disabled={bulkSaving}>Cancel</Button>
            <Button
              onClick={handleBulkPromote}
              disabled={bulkSaving || bulkIncluded.length === 0 || projectedW > 100.01 || projectedPub > 100.01 || projectedPerf > 100.01}
            >
              {bulkSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UserPlus className="h-3.5 w-3.5 mr-1" />}
              Promote {bulkIncluded.length} &amp; Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
